import { useTranslation } from "react-i18next";
import { all_routes } from "router/all_routes";
import { formatMoney } from "../../../../helpers/numberHelper";
import FeesStatusBadge from "../../../Common/Badge/FeesStatusBadge";
import Badge from "components/Common/ui/Badge";
import CustomButton from "components/Common/ui/CustomButton";
import PaymentMethodDisplay from "components/Common/ui/PaymentMethodDisplay";
import { formatToLocaleDate } from "utils/dateUtils";
import NoDataComponent from "components/Common/NoData";
import { useAbility } from "config/abilityContext";

interface Props {
  fees: any[];
  setSelectedFee: (fee: any) => void;
  setIsModalOpen: (isOpen: boolean) => void;
  handlePaymentClick: (fee: any) => void;
  handleDeletePayment: (paymentId: string) => void;
  showDeleteFeeConfirm: (feeId: string) => void;
  handleUpdateFeeAmount: (fee: any) => void;
}

const FeesContent = ({
  fees,
  setSelectedFee,
  setIsModalOpen,
  handlePaymentClick,
  handleDeletePayment,
  showDeleteFeeConfirm,
  handleUpdateFeeAmount,
}: Props) => {
  const { t } = useTranslation();
  const ability = useAbility();

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5>{t("fees_list")}</h5>
        {ability.can("create", "Fee") && (
          <CustomButton
            variant="primary"
            onClick={() => {
              setSelectedFee(null);
              setIsModalOpen(true);
            }}
          >
            <i className="ti ti-plus me-2" />
            {t("add_fees")}
          </CustomButton>
        )}
      </div>

      <div className="card-body">
        <div className="accordion" id="feesAccordion">
          {fees?.length === 0 ? (
            <NoDataComponent />
          ) : (
            fees?.map((fee: any) => (
              <div className="accordion-item" key={fee.id}>
                <h2 className="accordion-header">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse${fee.id}`}
                  >
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <div className="d-flex align-items-center">
                        <div className="me-4">
                          <div className="fw-bold">{fee?.feeType?.name}</div>
                          <small className="text-muted">
                            {t("due_date")}: {formatToLocaleDate(fee.dueDate)}
                          </small>
                        </div>
                        <div className="me-4">
                          <div className="fw-bold">{formatMoney(parseFloat(fee.amount))}</div>
                          <small className="text-muted">
                            {t("remaining")}: {formatMoney(fee.amountNotPaid)}
                          </small>
                        </div>
                      </div>

                      <div className="d-flex gap-2">
                        <FeesStatusBadge status={fee.computedStatus} />
                        {fee.status === "pending" && ability.can("update", "Fee") && (
                          <CustomButton
                            variant="warning"
                            size="sm"
                            onClick={() => handleUpdateFeeAmount(fee)}
                          >
                            <i className="ti ti-edit me-1" />
                            {t("update_fee_amount")}
                          </CustomButton>
                        )}

                        {fee.status === "pending" && ability.can("delete", "Fee") && (
                          <CustomButton
                            variant="danger"
                            size="sm"
                            onClick={() => showDeleteFeeConfirm(fee.id)}
                          >
                            <i className="ti ti-trash me-1" />
                            {t("delete")}
                          </CustomButton>
                        )}
                      </div>
                    </div>
                  </button>
                </h2>

                <div
                  id={`collapse${fee.id}`}
                  className="accordion-collapse collapse"
                  data-bs-parent="#feesAccordion"
                >
                  <div className="accordion-body">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="w-100">
                        <div className="d-flex justify-content-between mb-3">
                          <h6>{t("payment_history")}</h6>
                          <div>
                            {fee.status === "paid" ? (
                              <CustomButton
                                variant="success"
                                asLink={true}
                                to={all_routes.showFee.replace(":feeId", fee.id)}
                              >
                                <i className="ti ti-download me-1" />
                                {t("download_receipt")}
                              </CustomButton>
                            ) : (
                              <CustomButton
                                variant="primary"
                                onClick={() => handlePaymentClick(fee)}
                              >
                                <i className="ti ti-plus me-1" />
                                {t("add_payment")}
                              </CustomButton>
                            )}
                          </div>
                        </div>

                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th>{t("date")}</th>
                                <th>{t("amount")}</th>
                                <th>{t("payment_method")}</th>
                                <th>{t("status")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {fee.feePayments.length > 0 ? (
                                fee.feePayments.map((payment: any) => (
                                  <tr key={payment.id}>
                                    <td>{formatToLocaleDate(payment.createdAt)}</td>
                                    <td>{formatMoney(payment.amount)}</td>
                                    <td>
                                      <PaymentMethodDisplay payment={payment} />
                                    </td>
                                    <td>
                                      <Badge
                                        variant={payment.status === "completed" ? "success" : "warning"}
                                        text={payment.status === "completed" ? "paid" : payment.status}
                                        showIcon={false}
                                      />
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={4} className="text-center text-muted">
                                    {t("no_payments_yet")}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default FeesContent;
